import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import {
  C,
  E,
  MODERN,
  N,
  RETRO,
  clamp,
  lerp,
  mixColor,
  prog,
  rnd,
  spring,
  step,
  toPath,
  xf
} from '../../clip-geometry'
import { BX, BY, SH, SW } from '../stage'

// Variante "Karl Klammer": Retro-Klammer (1997) wickelt sich zur modernen Aivy-Klammer.

const INTRO_END = 5

const RETRO_POS = { x: BX, y: SH - 65, s: 0.5 }
const LINE = { x: BX, y: 196, len: 190 }
const MS = 0.28
const MODERN_POS = { x: BX + 3 * MS, y: BY - 13 * MS, s: MS }
const EYE_K = 1.35

const BURST = [
  { c: C.yellow, a: -150, d: 58, k: 'tri' },
  { c: C.green, a: -110, d: 62, k: 'sq' },
  { c: C.pink, a: -70, d: 56, k: 'dot' },
  { c: C.blue, a: -190, d: 50, k: 'sq' },
  { c: C.yellow, a: -30, d: 48, k: 'dot' }
]
const SPARK =
  'M0 -14 C1.6 -4 4 -1.6 14 0 C4 1.6 1.6 4 0 14 C-1.6 4 -4 1.6 -14 0 C-4 -1.6 -1.6 -4 0 -14Z'

const RETRO_FONT = 'Tahoma, Verdana, Geneva, sans-serif'

function renderFrame(t, el, ui) {
  const tr = t < 3.1 ? step(t) : t // Retro-Teil bewusst mit 12 fps

  // ---------- Retro-Klammer ----------
  const pop =
    tr < 0.05
      ? 0
      : tr < 0.22
        ? lerp(0, 1.15, prog(tr, 0.05, 0.22))
        : lerp(1.15, 1, prog(tr, 0.22, 0.32))
  const rock = ((3 * Math.PI) / 180) * Math.sin(tr * 2 * Math.PI * 0.9)
  const glitchOn = t > 2.8 && t < 3.15
  const gf = Math.floor(t * 24)
  const retroT = {
    x: RETRO_POS.x + (glitchOn ? (rnd(gf) - 0.5) * 6 : 0),
    y: RETRO_POS.y,
    s: RETRO_POS.s * pop,
    r: rock
  }

  // ---------- Moderne Klammer ----------
  const popT = t - 4.55
  const mPop =
    popT > 0 ? 1 + 0.14 * Math.exp(-5 * popT) * Math.sin(15 * popT) : 1
  const idle = clamp((t - 4.6) * 2)
  const bob = Math.sin(t * 2.1) * 1.5 * idle
  const hoverS = 1 + 0.07 * ui.hover
  const modernT = {
    x: MODERN_POS.x,
    y: MODERN_POS.y + bob - ui.hover * 1.5,
    s: MODERN_POS.s * mPop * hoverS,
    r: 0
  }

  // ---------- Draht-Morph: Retro → Linie → Modern ----------
  const uA = prog(t, 3.1, 3.65)
  const uB = prog(t, 3.9, 4.6)
  const retroPts = xf(RETRO, retroT)
  const modernPts = xf(MODERN, modernT)
  const twAmp =
    14 * Math.exp(-5.5 * Math.max(0, t - 3.65)) * prog(t, 3.55, 3.7) * (1 - uB)
  const tw = Math.sin((t - 3.6) * 2 * Math.PI * 7)
  const pts = new Array(N)
  for (let i = 0; i < N; i++) {
    const s = i / (N - 1)
    const lx = LINE.x + twAmp * Math.sin(s * Math.PI) * tw
    const ly = LINE.y - LINE.len / 2 + s * LINE.len
    const pa = E.inOutCubic(clamp((uA - s * 0.45) / 0.55))
    const pb = E.inOutCubic(clamp((uB - s * 0.4) / 0.6))
    pts[i] = [
      lerp(lerp(retroPts[i][0], lx, pa), modernPts[i][0], pb),
      lerp(lerp(retroPts[i][1], ly, pa), modernPts[i][1], pb)
    ]
  }
  el.wire.setAttribute('d', toPath(pts))
  el.wire.style.opacity = pop > 0 ? 1 : 0
  el.wire.setAttribute(
    'stroke',
    mixColor(C.wire, C.blue, E.inOutCubic(prog(uA, 0.15, 0.85)))
  )
  el.wire.setAttribute(
    'stroke-width',
    lerp(lerp(4.4 * pop, 4.2, E.outCubic(uA)), 3.6, E.outCubic(uB)).toFixed(2)
  )
  const hlO = pop > 0 ? 1 - prog(t, 3.0, 3.15) : 0
  el.hl.style.opacity = hlO
  if (hlO > 0)
    el.hl.setAttribute(
      'd',
      toPath(xf(RETRO, { ...retroT, x: retroT.x - 0.9, y: retroT.y - 0.9 }))
    )
  const gShow = glitchOn && rnd(gf + 3) > 0.35
  ;[el.gA, el.gB].forEach((g, k) => {
    g.style.opacity = gShow ? 1 : 0
    if (gShow) {
      const off = (k ? 1 : -1) * (2 + rnd(gf + k) * 4)
      g.setAttribute('d', toPath(xf(RETRO, { ...retroT, x: retroT.x + off })))
    }
  })

  // ---------- Retro-Gesicht ----------
  const faceS = pop * (1 - E.inCubic(prog(t, 2.95, 3.1)))
  el.rface.style.opacity = faceS > 0.01 ? 1 : 0
  el.rface.setAttribute(
    'transform',
    `translate(${retroT.x} ${retroT.y}) rotate(${(retroT.r * 180) / Math.PI}) scale(${RETRO_POS.s * faceS})`
  )
  let look = [0, 0]
  if (tr > 0.4) look = [-4, -3] // zur Sprechblase
  if (tr > 1.6) look = [-3, 2]
  if (tr > 2.2) look = [0, 0] // "Moment ..."
  const blink = (tr > 1.3 && tr < 1.42) || (tr > 2.55 && tr < 2.63) ? 0.12 : 1
  el.reyes.forEach((g) => {
    g.setAttribute(
      'transform',
      `translate(${g.dataset.x} ${g.dataset.y}) scale(1 ${blink})`
    )
    g.lastChild.setAttribute('transform', `translate(${look[0]} ${look[1]})`)
  })
  const raise = tr > 0.5 && tr < 0.9 ? -7 : tr > 2.2 ? -9 : -2
  el.brows.forEach((b) => b.setAttribute('transform', `translate(0 ${raise})`))

  // ---------- Retro-Sprechblase ----------
  const b1 = tr >= 0.45 && tr < 2.2
  const b2 = tr >= 2.2 && tr < 2.85
  el.bubble.style.opacity = b1 || b2 ? 1 : 0
  el.bubble.style.transform = `scale(${tr < 0.53 ? 0.6 : 1})`
  el.bubble1.style.display = b2 ? 'none' : 'block'
  el.bubble2.style.display = b2 ? 'block' : 'none'

  // ---------- Button-Kreis ----------
  const circ = spring(t - 4.3, 8, 14)
  el.circle.setAttribute('r', Math.max(0, 32 * circ * (1 + 0.04 * ui.hover)))
  el.circle.setAttribute('stroke', mixColor('#E3E6EC', C.blue, ui.hover))

  // ---------- Burst ----------
  el.burst.forEach((g, i) => {
    const b = BURST[i]
    const p = E.outExpo(prog(t, 4.55 + i * 0.03, 5.0))
    const out = 1 - prog(t, 4.85, 5.35)
    if (t < 4.55 || out <= 0) {
      g.style.opacity = 0
      return
    }
    const a = (b.a * Math.PI) / 180
    const x = BX + Math.cos(a) * b.d * p
    const y = BY + Math.sin(a) * b.d * p
    g.style.opacity = 1
    g.setAttribute(
      'transform',
      `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${(p * 140).toFixed(0)}) scale(${(out * clamp((t - 4.55) * 8)).toFixed(3)})`
    )
  })

  // ---------- Modernes Gesicht ----------
  const eyeOpen = spring(t - 4.6, 9, 16)
  let mBlink = 1
  if (t > INTRO_END) {
    const ph = (t - INTRO_END) % 4.6
    if (ph > 4.42) mBlink = 0.12
  }
  const lookX = -ui.hover * 1.2
  const lookY = -ui.hover * 1.2 - ui.open * 2
  el.mface.style.opacity = t > 4.55 ? 1 : 0
  el.mface.setAttribute(
    'transform',
    `translate(${modernT.x} ${modernT.y}) scale(${modernT.s})`
  )
  ;[
    [-1, -26],
    [17, -26]
  ].forEach(([ex, ey], i) => {
    el.meyes[i].setAttribute(
      'transform',
      `translate(${ex + lookX} ${ey + lookY}) scale(${EYE_K} ${(EYE_K * Math.max(0, eyeOpen * mBlink)).toFixed(3)})`
    )
  })
  const sp1 = spring(t - 4.75, 8, 14)
  const sp2 = spring(t - 4.9, 8, 14)
  const tw1 = 1 + 0.15 * Math.sin(t * 3.1) + 0.25 * ui.open
  el.spark1.setAttribute(
    'transform',
    `translate(58 -82) rotate(${(1 - sp1) * -120}) scale(${(sp1 * tw1 * 1.7).toFixed(3)})`
  )
  el.spark2.setAttribute(
    'transform',
    `translate(78 -52) rotate(${(1 - sp2) * -120}) scale(${(sp2 * 0.9).toFixed(3)})`
  )
}

/** Kleiner statischer Avatar mit der modernen Klammer, z. B. fuer Chat-Header */
export function ClipAvatar({ size = 36 }) {
  const s = 0.4
  const X0 = 50 + 3 * s
  const Y0 = 50 - 13 * s
  const d = toPath(xf(MODERN, { x: X0, y: Y0, s }))
  return (
    <svg width={size} height={size} viewBox='0 0 100 100' aria-hidden='true'>
      <circle cx='50' cy='50' r='50' fill={C.blue} />
      <path
        d={d}
        fill='none'
        stroke='#fff'
        strokeWidth='6.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      {[-1.5, 17.5].map((ex) => (
        <rect
          key={ex}
          x={X0 + ex * s - 2.6}
          y={Y0 - 24 * s - 4.8}
          width='5.2'
          height='9.6'
          rx='2.6'
          fill='#fff'
        />
      ))}
    </svg>
  )
}

ClipAvatar.propTypes = { size: PropTypes.number }

const ClipStage = forwardRef(function ClipStage(_, ref) {
  const svgRef = useRef(null)
  const bubbleRef = useRef(null)
  const elRef = useRef(null)

  useLayoutEffect(() => {
    const svg = svgRef.current
    const bubble = bubbleRef.current
    const q = (s) => svg.querySelector(s)
    const qa = (s) => Array.from(svg.querySelectorAll(s))
    elRef.current = {
      wire: q('.al-wire'),
      hl: q('.al-hl'),
      gA: q('.al-gA'),
      gB: q('.al-gB'),
      rface: q('.al-rface'),
      reyes: qa('.al-reye'),
      brows: qa('.al-brow'),
      mface: q('.al-mface'),
      meyes: qa('.al-meye'),
      spark1: q('.al-spark1'),
      spark2: q('.al-spark2'),
      circle: q('.al-circle'),
      burst: qa('.al-burst > g'),
      bubble,
      bubble1: bubble.querySelector('.al-b1'),
      bubble2: bubble.querySelector('.al-b2')
    }
  }, [])

  useImperativeHandle(ref, () => ({
    frame: (t, ui) => elRef.current && renderFrame(t, elRef.current, ui)
  }))

  return (
    <>
      <svg
        ref={svgRef}
        width={SW}
        height={SH}
        viewBox={`0 0 ${SW} ${SH}`}
        className='absolute inset-0 overflow-visible'
        aria-hidden='true'
      >
        <g className='al-burst'>
          {BURST.map((b, i) => (
            <g key={i} opacity='0'>
              {b.k === 'dot' && <circle r='3.2' fill={b.c} />}
              {b.k === 'sq' && (
                <rect x='-3' y='-3' width='6' height='6' rx='1' fill={b.c} />
              )}
              {b.k === 'tri' && (
                <path
                  d='M0 -4.4 L4 2.6 L-4 2.6 Z'
                  fill={b.c}
                  stroke={b.c}
                  strokeLinejoin='round'
                />
              )}
            </g>
          ))}
        </g>
        <circle
          className='al-circle'
          cx={BX}
          cy={BY}
          r='0'
          fill='#fff'
          strokeWidth='1.5'
        />
        {['al-gA', 'al-gB'].map((c, i) => (
          <path
            key={c}
            className={c}
            fill='none'
            stroke={i ? C.green : C.pink}
            strokeWidth='4'
            strokeLinecap='round'
            strokeLinejoin='round'
            opacity='0'
          />
        ))}
        <path
          className='al-wire'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          opacity='0'
        />
        <path
          className='al-hl'
          fill='none'
          stroke='#E6E9EE'
          strokeWidth='1.2'
          strokeLinecap='round'
          strokeLinejoin='round'
          opacity='0'
        />
        <g className='al-rface' opacity='0'>
          {[
            [-20, -52],
            [16, -58]
          ].map(([x, y]) => (
            <g key={x} className='al-reye' data-x={x} data-y={y}>
              <circle r='16' fill='#fff' stroke='#222' strokeWidth='2.4' />
              <circle r='6.2' fill='#111' />
            </g>
          ))}
          <path
            className='al-brow'
            d='M-33 -73 Q-20 -82 -7 -76'
            fill='none'
            stroke='#111'
            strokeWidth='4.5'
            strokeLinecap='round'
          />
          <path
            className='al-brow'
            d='M3 -82 Q16 -89 29 -80'
            fill='none'
            stroke='#111'
            strokeWidth='4.5'
            strokeLinecap='round'
          />
        </g>
        <g className='al-mface' opacity='0'>
          {[0, 1].map((i) => (
            <rect
              key={i}
              className='al-meye'
              x='-4.5'
              y='-8.5'
              width='9'
              height='17'
              rx='4.5'
              fill={C.dark}
            />
          ))}
          <path className='al-spark1' d={SPARK} fill={C.yellow} />
          <path className='al-spark2' d={SPARK} fill={C.yellow} />
        </g>
      </svg>

      {/* Retro-Sprechblase (1997) */}
      <div
        ref={bubbleRef}
        className='absolute'
        style={{
          right: 70,
          bottom: 118,
          width: 214,
          opacity: 0,
          transformOrigin: '100% 100%',
          background: '#FFFFE1',
          border: '1px solid #000',
          borderRadius: 9,
          padding: '10px 12px',
          fontFamily: RETRO_FONT,
          fontSize: 12,
          lineHeight: 1.35,
          color: '#000',
          WebkitFontSmoothing: 'none'
        }}
      >
        <div className='al-b1'>
          <p style={{ margin: '0 0 7px' }}>
            Es sieht so aus, als würden Sie Talente suchen. Möchten Sie Hilfe?
          </p>
          {['Ja, bitte helfen', 'Nein, danke'].map((o) => (
            <div
              key={o}
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                marginTop: 2
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: '#0000ff'
                }}
              />
              <span>{o}</span>
            </div>
          ))}
        </div>
        <div className='al-b2' style={{ display: 'none', fontWeight: 700 }}>
          Moment… ich hab ein Update.
        </div>
        <svg
          viewBox='0 0 26 22'
          width='26'
          height='22'
          style={{ position: 'absolute', right: -25, bottom: 10 }}
        >
          <path d='M0 0 L26 13 L0 20' fill='#FFFFE1' stroke='#000' />
          <rect x='-2' y='0.6' width='3' height='18.8' fill='#FFFFE1' />
        </svg>
      </div>
    </>
  )
})

export default {
  id: 'klammer',
  name: 'Karl Klammer',
  description:
    'Karl aus Office 97 wird zur modernen Aivy-Klammer. Jeder erkennt sofort, was gemeint ist.',
  introEnd: INTRO_END,
  evolved: true,
  storageKey: 'aivy.assistantLauncher.introSeen',
  Stage: ClipStage,
  Avatar: ClipAvatar
}
